import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useBusiness } from '@/providers/business-provider';
import { apiRequest } from '@/lib/queryClient';
import { Sidebar } from '@/components/ui/sidebar';
import {
  Users,
  DollarSign,
  Calendar,
  RefreshCw,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  Building2,
  Receipt,
  FileText,
  Loader2,
  ArrowUpCircle,
  Shield,
  Unplug,
  Activity,
} from 'lucide-react';
import { Link } from 'wouter';

// ─── Types matching backend responses ───────────────────────────────────

interface PayrollProvider {
  id: string;
  name: string;
  logo: string;
}

interface PayrollConnection {
  id: number;
  business_id: number;
  provider: string;
  status: 'active' | 'pending' | 'error' | 'disconnected';
  realm_id: string | null;
  company_name: string | null;
  last_sync_at: string | null;
  sync_frequency: string;
  auto_sync: boolean;
  created_at: string;
  updated_at: string;
}

interface SyncLog {
  id: number;
  business_id: number;
  provider: string;
  sync_type: string;
  status: 'success' | 'error' | 'partial';
  employees_synced: number;
  payroll_runs_synced: number;
  error: string | null;
  duration_ms: number;
  synced_at: string;
}

interface SyncSummary {
  employeeCount: number;
  payrollRunCount: number;
  lastSync: SyncLog | null;
}

interface Employee {
  id: number;
  provider: string;
  external_id: string;
  name: string;
  email: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface PayrollRun {
  id: number;
  provider: string;
  external_id: string;
  pay_date: string;
  gross_pay: string;
  taxes: string;
  net_pay: string;
  employee_count: number;
  journal_entry_id: number | null;
  created_at: string;
}

interface ExpenseReport {
  id: number;
  reportNumber: string;
  employeeName: string;
  title: string;
  totalAmount: number;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
  submittedDate: string;
  approvedDate: string | null;
  itemCount: number;
}

interface PlanAccess {
  hasAccess: boolean;
  allowedProviders: string[];
  planName: string;
  upgradeUrl: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────

const providerLabel: Record<string, string> = {
  quickbooks: 'QuickBooks',
  gusto: 'Gusto',
  adp: 'ADP',
  paychex: 'Paychex',
};

function formatCurrency(amount: number | string) {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function nextSyncTime(lastSync: string | null): string {
  if (!lastSync) return 'Scheduled within 1 hour';
  const last = new Date(lastSync);
  // Next sync = tomorrow at same time (daily sync)
  const next = new Date(last);
  next.setDate(next.getDate() + 1);
  if (next <= new Date()) return 'Scheduled within 1 hour';
  return next.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ─── Component ──────────────────────────────────────────────────────────

export default function PayrollIntegration() {
  const { activeBusiness } = useBusiness();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [providers, setProviders] = useState<PayrollProvider[]>([]);
  const [connections, setConnections] = useState<PayrollConnection[]>([]);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [syncSummary, setSyncSummary] = useState<SyncSummary>({ employeeCount: 0, payrollRunCount: 0, lastSync: null });
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [payrollRuns, setPayrollRuns] = useState<PayrollRun[]>([]);
  const [expenseReports, setExpenseReports] = useState<ExpenseReport[]>([]);
  const [planAccess, setPlanAccess] = useState<PlanAccess>({ hasAccess: true, allowedProviders: ['quickbooks', 'gusto'], planName: '', upgradeUrl: '/subscription' });
  const [showProviderDialog, setShowProviderDialog] = useState(false);

  const fetchPayrollData = useCallback(async () => {
    if (!activeBusiness) return;
    try {
      setLoading(true);
      const [providersRes, connectionsRes, logsRes, summaryRes, employeesRes, runsRes, expensesRes, planRes] = await Promise.all([
        apiRequest('GET', '/api/payroll/providers'),
        apiRequest('GET', `/api/businesses/${activeBusiness.id}/payroll/connections`),
        apiRequest('GET', `/api/businesses/${activeBusiness.id}/payroll/sync-logs`),
        apiRequest('GET', `/api/businesses/${activeBusiness.id}/payroll/sync-summary`),
        apiRequest('GET', `/api/businesses/${activeBusiness.id}/payroll/employees`),
        apiRequest('GET', `/api/businesses/${activeBusiness.id}/payroll/runs`),
        apiRequest('GET', `/api/businesses/${activeBusiness.id}/expense-reports`).catch(() => null),
        apiRequest('GET', `/api/businesses/${activeBusiness.id}/payroll/plan-access`),
      ]);

      setProviders(await providersRes.json());
      setConnections(await connectionsRes.json());
      setSyncLogs(await logsRes.json());
      setSyncSummary(await summaryRes.json());
      setEmployees(await employeesRes.json());
      setPayrollRuns(await runsRes.json());
      if (expensesRes) setExpenseReports(await expensesRes.json());
      setPlanAccess(await planRes.json());
    } catch (error) {
      console.error('Failed to fetch payroll data:', error);
      toast({ title: 'Error', description: 'Failed to load payroll data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [activeBusiness, toast]);

  useEffect(() => {
    if (activeBusiness) fetchPayrollData();
  }, [activeBusiness, fetchPayrollData]);

  // Handle OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectedProvider = params.get('connected');
    if (connectedProvider === 'quickbooks' || connectedProvider === 'gusto') {
      toast({ title: `${providerLabel[connectedProvider] || connectedProvider} Connected`, description: 'Account linked. Auto-sync will start within 1 hour.' });
      window.history.replaceState({}, '', '/payroll');
      if (activeBusiness) fetchPayrollData();
    }
    if (params.get('error')) {
      toast({ title: 'Connection Failed', description: decodeURIComponent(params.get('error') || 'Unknown error'), variant: 'destructive' });
      window.history.replaceState({}, '', '/payroll');
    }
  }, []);

  const connectProvider = async (providerId: string) => {
    if (!activeBusiness) return;
    try {
      setLoading(true);

      if (!planAccess.hasAccess) {
        toast({ title: 'Plan Upgrade Required', description: 'Upgrade your plan to use payroll automation', variant: 'destructive' });
        return;
      }
      if (!planAccess.allowedProviders.includes(providerId)) {
        toast({ title: 'Not Available', description: `${providerLabel[providerId] || providerId} requires a higher plan`, variant: 'destructive' });
        return;
      }

      if (providerId === 'adp' || providerId === 'paychex' || providerId === 'surepayroll') {
        toast({ title: 'Coming Soon', description: `${providerLabel[providerId] || providerId} integration requires enterprise approval.` });
        return;
      }

      const response = await fetch(`/api/businesses/${activeBusiness.id}/payroll/connect/${providerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const result = await response.json();

      if (result.code === 'PLAN_UPGRADE_REQUIRED') {
        toast({ title: 'Plan Upgrade Required', description: result.message, variant: 'destructive' });
        return;
      }

      if (result.url) {
        window.location.href = result.url;
        return;
      }
      throw new Error(result.message || result.error || 'Failed to connect');
    } catch (error: any) {
      toast({ title: 'Connection Failed', description: error?.message || 'Failed to connect provider', variant: 'destructive' });
    } finally {
      setLoading(false);
      setShowProviderDialog(false);
    }
  };

  const disconnectProvider = async (provider: string) => {
    if (!activeBusiness) return;
    try {
      await apiRequest('POST', `/api/businesses/${activeBusiness.id}/payroll/${provider}/disconnect`, {});
      toast({ title: 'Disconnected', description: `${providerLabel[provider] || provider} has been disconnected.` });
      fetchPayrollData();
    } catch {
      toast({ title: 'Error', description: 'Failed to disconnect', variant: 'destructive' });
    }
  };

  const toggleAutoSync = async (connectionId: number, value: boolean) => {
    if (!activeBusiness) return;
    try {
      await apiRequest('PATCH', `/api/businesses/${activeBusiness.id}/payroll/connections/${connectionId}/auto-sync`, { auto_sync: value });
      setConnections(prev => prev.map(c => c.id === connectionId ? { ...c, auto_sync: value } : c));
      toast({ title: value ? 'Auto-Sync Enabled' : 'Auto-Sync Disabled' });
    } catch {
      toast({ title: 'Error', description: 'Failed to update auto-sync', variant: 'destructive' });
    }
  };

  const bulkSyncAll = async () => {
    try {
      setBulkSyncing(true);
      const res = await apiRequest('POST', '/api/payroll/sync-all', {});
      const result = await res.json();
      toast({ title: 'Bulk Sync Complete', description: `${result.succeeded} synced, ${result.failed} failed, ${result.skipped} skipped of ${result.total}` });
      fetchPayrollData();
    } catch (error: any) {
      toast({ title: 'Bulk Sync Failed', description: error?.message || 'Failed', variant: 'destructive' });
    } finally {
      setBulkSyncing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'default', success: 'default', paid: 'default', approved: 'default',
      pending: 'secondary', partial: 'secondary',
      error: 'destructive', rejected: 'destructive', disconnected: 'outline', draft: 'outline', inactive: 'outline',
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  // ─── Plan-gated wrapper ───────────────────────────────────────────────

  if (!activeBusiness) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold">No Business Selected</h2>
            <p className="text-muted-foreground">Please select a business to manage payroll.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!planAccess.hasAccess && planAccess.planName) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="max-w-xl mx-auto mt-20">
            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Payroll Automation — Plan Upgrade Required
                </CardTitle>
                <CardDescription>
                  Your current plan ({planAccess.planName}) doesn't include payroll automation.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Upgrade to sync payroll data from QuickBooks, Gusto, and more.
                  Journal entries are created automatically from payroll runs.
                </p>
              </CardContent>
              <CardFooter>
                <Button asChild>
                  <Link to={planAccess.upgradeUrl}>
                    <ArrowUpCircle className="w-4 h-4 mr-2" />
                    Upgrade Subscription
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ─── Active connections ───────────────────────────────────────────────
  const activeConnections = connections.filter(c => c.status === 'active');
  const lastLog = syncSummary.lastSync;

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="flex-1 ml-64 p-8">
        <div className="max-w-7xl mx-auto space-y-8">

          {/* ── Header ─────────────────────────────────────────── */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Payroll Integration</h1>
              <p className="text-muted-foreground">
                Payroll syncs automatically once per day. Connect a provider to get started.
              </p>
            </div>
            <div className="flex gap-2">
              {/* Admin bulk sync */}
              <Button variant="outline" onClick={bulkSyncAll} disabled={bulkSyncing}>
                {bulkSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                {bulkSyncing ? 'Syncing All...' : 'Sync All Clients'}
              </Button>
              <Dialog open={showProviderDialog} onOpenChange={setShowProviderDialog}>
                <DialogTrigger asChild>
                  <Button><Plus className="w-4 h-4 mr-2" />Connect Provider</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Connect Payroll Provider</DialogTitle>
                    <DialogDescription>Choose a provider to integrate with your accounting system</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {providers.map((provider) => {
                      const allowed = planAccess.allowedProviders.includes(provider.id);
                      return (
                        <Card
                          key={provider.id}
                          className={`cursor-pointer transition-colors ${allowed ? 'hover:bg-accent' : 'opacity-50'}`}
                          onClick={() => allowed ? connectProvider(provider.id) : undefined}
                        >
                          <CardContent className="p-6 text-center">
                            <Building2 className="w-12 h-12 mx-auto mb-4 text-primary" />
                            <h3 className="font-semibold">{provider.name}</h3>
                            {!allowed && <p className="text-xs text-muted-foreground mt-1">Requires plan upgrade</p>}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* ── Sync Summary Cards ─────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Connections</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{activeConnections.length}</div>
                <p className="text-xs text-muted-foreground">{connections.length} total</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Employees Synced</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{syncSummary.employeeCount}</div>
                <p className="text-xs text-muted-foreground">From all providers</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Payroll Runs</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{syncSummary.payrollRunCount}</div>
                <p className="text-xs text-muted-foreground">Journal entries auto-created</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Last Sync</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold flex items-center gap-2">
                  {lastLog ? getStatusBadge(lastLog.status) : <span className="text-muted-foreground text-base">—</span>}
                </div>
                <p className="text-xs text-muted-foreground">
                  {lastLog ? relativeTime(lastLog.synced_at) : 'No syncs yet'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* ── Tabs ───────────────────────────────────────────── */}
          <Tabs defaultValue="connections" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="connections">Connections</TabsTrigger>
              <TabsTrigger value="employees">Employees</TabsTrigger>
              <TabsTrigger value="payroll">Payroll Runs</TabsTrigger>
              <TabsTrigger value="logs">Sync Logs</TabsTrigger>
              <TabsTrigger value="reports">Reports</TabsTrigger>
            </TabsList>

            {/* ── Connections Tab ──────────────────────────────── */}
            <TabsContent value="connections" className="space-y-4">
              {connections.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-medium mb-2">No Connections</h3>
                    <p className="text-muted-foreground mb-4">Connect a payroll provider to get started</p>
                    <Button onClick={() => setShowProviderDialog(true)}><Plus className="w-4 h-4 mr-2" />Connect Provider</Button>
                  </CardContent>
                </Card>
              ) : (
                connections.map((conn) => (
                  <Card key={conn.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        {/* Left: info */}
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{providerLabel[conn.provider] || conn.provider}</h3>
                              {getStatusBadge(conn.status)}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {conn.company_name || conn.realm_id || 'No company info'}
                            </p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Last sync: {relativeTime(conn.last_sync_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Next: {conn.auto_sync ? nextSyncTime(conn.last_sync_at) : 'Disabled'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right: controls */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={conn.auto_sync !== false}
                              onCheckedChange={(v) => toggleAutoSync(conn.id, v)}
                            />
                            <span className="text-sm text-muted-foreground">Auto Sync</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => disconnectProvider(conn.provider)}
                          >
                            <Unplug className="w-4 h-4 mr-1" />
                            Disconnect
                          </Button>
                        </div>
                      </div>

                      {/* Error banner */}
                      {conn.status === 'error' && (
                        <div className="mt-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 flex-shrink-0" />
                          Connection error — token may have expired. Please reconnect.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* ── Employees Tab ────────────────────────────────── */}
            <TabsContent value="employees" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Synced Employees</CardTitle>
                  <CardDescription>Employees pulled from connected payroll providers</CardDescription>
                </CardHeader>
                <CardContent>
                  {employees.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No employees synced yet. Data will appear after the first auto-sync.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Last Updated</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employees.map((emp) => (
                          <TableRow key={emp.id}>
                            <TableCell className="font-medium">{emp.name}</TableCell>
                            <TableCell>{emp.email || '—'}</TableCell>
                            <TableCell>{providerLabel[emp.provider] || emp.provider}</TableCell>
                            <TableCell>{getStatusBadge(emp.status)}</TableCell>
                            <TableCell>{relativeTime(emp.updated_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Payroll Runs Tab ─────────────────────────────── */}
            <TabsContent value="payroll" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Payroll Runs</CardTitle>
                  <CardDescription>Synced payroll data with auto-generated journal entries</CardDescription>
                </CardHeader>
                <CardContent>
                  {payrollRuns.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No payroll runs synced yet.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Pay Date</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>Employees</TableHead>
                          <TableHead>Gross Pay</TableHead>
                          <TableHead>Taxes</TableHead>
                          <TableHead>Net Pay</TableHead>
                          <TableHead>Journal</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payrollRuns.map((run) => (
                          <TableRow key={run.id}>
                            <TableCell>{new Date(run.pay_date).toLocaleDateString()}</TableCell>
                            <TableCell>{providerLabel[run.provider] || run.provider}</TableCell>
                            <TableCell>{run.employee_count}</TableCell>
                            <TableCell>{formatCurrency(run.gross_pay)}</TableCell>
                            <TableCell>{formatCurrency(run.taxes)}</TableCell>
                            <TableCell className="font-medium">{formatCurrency(run.net_pay)}</TableCell>
                            <TableCell>
                              {run.journal_entry_id
                                ? <Badge variant="default"><CheckCircle className="w-3 h-3 mr-1" />JE #{run.journal_entry_id}</Badge>
                                : <Badge variant="outline">Pending</Badge>
                              }
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Sync Logs Tab ────────────────────────────────── */}
            <TabsContent value="logs" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Sync Activity Log</CardTitle>
                  <CardDescription>History of automatic payroll syncs</CardDescription>
                </CardHeader>
                <CardContent>
                  {syncLogs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No sync activity yet.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Employees</TableHead>
                          <TableHead>Runs</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {syncLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>{new Date(log.synced_at).toLocaleString()}</TableCell>
                            <TableCell>{providerLabel[log.provider] || log.provider}</TableCell>
                            <TableCell><Badge variant="outline">{log.sync_type}</Badge></TableCell>
                            <TableCell>{getStatusBadge(log.status)}</TableCell>
                            <TableCell>{log.employees_synced}</TableCell>
                            <TableCell>{log.payroll_runs_synced}</TableCell>
                            <TableCell>{log.duration_ms ? `${(log.duration_ms / 1000).toFixed(1)}s` : '—'}</TableCell>
                            <TableCell className="max-w-[200px] truncate text-destructive text-xs">
                              {log.error || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Reports Tab ──────────────────────────────────── */}
            <TabsContent value="reports" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Payroll Summary</CardTitle>
                    <CardDescription>Totals from synced payroll runs</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span>Total Gross Pay</span>
                        <span className="font-medium">{formatCurrency(payrollRuns.reduce((s, r) => s + parseFloat(r.gross_pay || '0'), 0))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Taxes</span>
                        <span className="font-medium">{formatCurrency(payrollRuns.reduce((s, r) => s + parseFloat(r.taxes || '0'), 0))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Net Pay</span>
                        <span className="font-medium">{formatCurrency(payrollRuns.reduce((s, r) => s + parseFloat(r.net_pay || '0'), 0))}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Expense Summary</CardTitle>
                    <CardDescription>Employee expense reports overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span>Total Submitted</span>
                        <span className="font-medium">{formatCurrency(expenseReports.reduce((s, r) => s + r.totalAmount, 0))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Approved</span>
                        <span className="font-medium">{formatCurrency(expenseReports.filter(r => r.status === 'approved').reduce((s, r) => s + r.totalAmount, 0))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Pending Review</span>
                        <span className="font-medium">{formatCurrency(expenseReports.filter(r => r.status === 'submitted').reduce((s, r) => s + r.totalAmount, 0))}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* ── Loading overlay ─────────────────────────────────── */}
          {loading && (
            <div className="fixed inset-0 bg-background/50 z-50 flex items-center justify-center">
              <div className="flex items-center gap-3 bg-card p-6 rounded-lg shadow-lg border">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="text-lg font-medium">Loading payroll data...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
